import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

export default function NewsletterSection() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError(t("newsletter.required"));
      return;
    }
    if (!email.includes("@")) {
      setError(t("newsletter.invalidEmail"));
      return;
    }
    setError("");
    try {
      const { error: insertError } = await supabase.from("newsletter_subscribers").insert({
        id: crypto.randomUUID(),
        name: name.trim(),
        email: email.trim(),
        status: "subscribed",
        created_at: new Date().toISOString(),
      });
      if (insertError) throw insertError;

      // Best-effort welcome email via Resend (silently no-op if not configured).
      supabase.functions.invoke("send-email", {
        body: {
          to: email.trim(),
          subject: "L+TV TÉLÉVISION — " + t("newsletter.welcomeSubject"),
          html: `<div style="font-family:Arial,sans-serif;max-width:520px;margin:0 auto;padding:24px;color:#1f2937;"><h1 style="font-size:20px;margin:0 0 8px;">L+TV TÉLÉVISION</h1><p style="font-size:14px;color:#6b7280;">${t("newsletter.welcomeBody")}</p></div>`,
        },
      }).catch(() => {});

      setSubmitted(true);
      setName("");
      setEmail("");
    } catch {
      setError(t("newsletter.error"));
    }
  };

  return (
    <section className="bg-primary-900 py-12 md:py-16">
      <div className="ltv-container">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-12 h-12 bg-accent-500 rounded-lg flex items-center justify-center text-white mx-auto mb-4">
            <i className="ri-mail-send-line text-xl"></i>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {t("home.subscribeTitle")}
          </h2>
          <p className="text-white/70 mb-8">
            {t("home.subscribeDesc")}
          </p>

          {submitted ? (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white mx-auto mb-3">
                <i className="ri-check-line text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">{t("home.subscribeSuccess")}</h3>
              <p className="text-white/70 text-sm">
                {t("home.subscribeSuccessDesc")}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder={t("home.yourName")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-md text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400"
              />
              <input
                type="email"
                placeholder={t("home.yourEmail")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-md text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400"
              />
              <button
                type="submit"
                className="bg-accent-500 hover:bg-accent-600 text-white font-semibold px-6 py-3 rounded-md transition-colors whitespace-nowrap"
              >
                {t("home.subscribe")}
              </button>
            </form>
          )}

          {error && (
            <p className="mt-3 text-accent-300 text-sm">{error}</p>
          )}

          <p className="text-white/40 text-xs mt-4">
            {t("home.bySubscribing")}
          </p>
        </div>
      </div>
    </section>
  );
}